import Layout from "@/layouts/layout";
import PageHeading from "@/components/layout/PageHeading";
import formatTime from "@/utils/formatTime";
import clockIcon from "@/assets/icons/clock.svg";
import { usePage } from "@inertiajs/react";
import Panel from "@/components/ui/Panel";
import Badge from "@/components/ui/Badge";
import Icon from "@/components/ui/Icon";
import Avatar from "@/components/ui/Avatar";
import { ButtonAnchor, ButtonLink } from "@/components/ui/Button";
import type { Project } from "@/interfaces/project";
import type { PublicUser } from "@/interfaces/user";

interface Props {
  project: Project & { user: PublicUser | null };
}

export default function DisplayProject({ project }: Props) {
  const { user } = usePage<{ user?: { is_admin: boolean } }>().props;
  const isAdmin = user?.is_admin;

  return (
    <Layout>
      <div className="flex flex-col gap-6">
        <PageHeading
          eyebrow="Projects"
          title={project.title ?? "Untitled project"}
        />

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {project.screenshot && (
            <Panel
              title="screenshot.png"
              bodyClassName="p-3"
              className="lg:row-span-2"
            >
              <img
                src={project.screenshot}
                alt={project.title ?? "Project screenshot"}
                className="border-line w-full border object-cover"
              />
            </Panel>
          )}

          <Panel title="about.txt" tone="alt">
            <div className="flex flex-col gap-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                {project.user && (
                  <div className="flex items-center gap-2.5">
                    <Avatar
                      src={project.user.avatar}
                      name={project.user.username}
                      size="sm"
                    />
                    <span className="text-body text-ink">
                      by {project.user.username}
                    </span>
                  </div>
                )}
                <span className="text-ink-muted flex items-center gap-1.5">
                  <Icon
                    src={clockIcon}
                    label="Hours approved"
                    className="h-4 w-4"
                  />
                  <span className="font-display text-body font-bold">
                    {formatTime(project.approved_seconds)}
                  </span>
                </span>
              </div>

              {project.high_quality && <Badge tone="brand">High quality</Badge>}

              {project.desc && (
                <p className="text-body text-ink-muted whitespace-pre-wrap">
                  {project.desc}
                </p>
              )}

              <div className="flex flex-col gap-3 sm:flex-row">
                {project.demo_link && (
                  <ButtonAnchor
                    href={project.demo_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="sm:flex-1"
                  >
                    Try the demo
                  </ButtonAnchor>
                )}
                {project.repo_link && (
                  <ButtonAnchor
                    href={project.repo_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="secondary"
                    className="sm:flex-1"
                  >
                    Read the code
                  </ButtonAnchor>
                )}
              </div>

              {isAdmin && (
                <ButtonLink
                  href={`/projects/${project.id}/manage`}
                  variant="ghost"
                  size="sm"
                  className="self-start"
                >
                  Admin view
                </ButtonLink>
              )}
            </div>
          </Panel>
        </div>
      </div>
    </Layout>
  );
}
